import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2,
  Sparkles,
  Zap,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Wrench,
  FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import EvidenceSourceDialog from './EvidenceSourceDialog';
import EvidenceSnapshotDialog from './EvidenceSnapshotDialog';

const API = '/api';

const statusMeta = {
  pass: { label: 'Satisfied', className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: '✓' },
  fail: { label: 'Not Satisfied', className: 'bg-red-100 text-red-800 border-red-300', icon: '✕' },
  partial: { label: 'Partially Satisfied', className: 'bg-amber-100 text-amber-800 border-amber-300', icon: '◐' },
  insufficient_evidence: { label: 'Insufficient Evidence', className: 'bg-orange-100 text-orange-800 border-orange-300', icon: '⚠' },
  not_applicable: { label: 'Not Applicable', className: 'bg-slate-100 text-slate-600 border-slate-300', icon: '—' },
  pending: { label: 'Pending Evaluation', className: 'bg-slate-100 text-slate-500 border-slate-200', icon: '…' },
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ───────────────────── Per-signal remediation accordion ─────────────────────

const SignalRemediation = ({ signal, controlId }) => {
  const [open, setOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiError, setAiError] = useState(null);

  const rem = signal.remediation || {};
  const isFailing = ['fail', 'partial', 'insufficient_evidence'].includes(signal.status);

  const generateAiPlan = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const resp = await axios.post(
        `${API}/controls/${controlId}/remediation/ai-plan`,
        { check_id: signal.check_id },
        authHeaders()
      );
      if (resp.data.ai_failed) {
        setAiError(resp.data.error || 'AI plan unavailable');
      } else {
        setAiText(resp.data.context || '');
      }
    } catch (err) {
      setAiError(err.response?.data?.detail || err.message);
    } finally {
      setAiLoading(false);
    }
  };

  if (!isFailing) {
    // Nothing to fix — don't render an accordion for passing / n-a signals
    return null;
  }

  return (
    <div className="mt-2 rounded border border-orange-200 bg-orange-50/40">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-medium text-orange-800 hover:bg-orange-50"
        data-testid={`remediation-toggle-${signal.check_id}`}
      >
        <span className="flex items-center gap-2">
          <Wrench className="w-3.5 h-3.5" />
          How to fix this — {rem.title || 'Remediation steps'}
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="px-4 py-3 space-y-3 border-t border-orange-200 bg-white">
          {rem.why_it_matters && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Why this matters
              </p>
              <p className="text-xs text-slate-700 leading-relaxed">{rem.why_it_matters}</p>
            </div>
          )}

          {Array.isArray(rem.steps) && rem.steps.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Steps
              </p>
              <ol className="list-decimal pl-5 space-y-1 text-xs text-slate-800">
                {rem.steps.map((s, i) => (
                  <li key={i} className="leading-relaxed">{s}</li>
                ))}
              </ol>
            </div>
          )}

          {rem.verification && (
            <div className="rounded bg-slate-50 border border-slate-200 p-2">
              <p className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">
                Verify
              </p>
              <p className="text-xs text-slate-700">{rem.verification}</p>
            </div>
          )}

          {rem.docs_url && (
            <a
              href={rem.docs_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900"
            >
              <ExternalLink className="w-3 h-3" />
              Vendor documentation
            </a>
          )}

          {/* AI tailored plan */}
          <div className="pt-2 border-t border-slate-200">
            {aiText ? (
              <div className="rounded-md bg-violet-50 border border-violet-200 p-3">
                <p className="text-[11px] uppercase tracking-wide font-medium text-violet-700 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Tailored plan for your environment
                </p>
                <p className="text-xs text-slate-800 whitespace-pre-wrap">{aiText}</p>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={generateAiPlan}
                disabled={aiLoading}
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                data-testid={`remediation-ai-btn-${signal.check_id}`}
              >
                {aiLoading ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> Generate tailored plan</>
                )}
              </Button>
            )}
            {aiError && <p className="text-xs text-red-600 mt-2">{aiError}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// ───────────────────────────── Main panel ─────────────────────────────

export const AutoEvidencePanel = ({ control, onEvaluated }) => {
  const [recollecting, setRecollecting] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [signals, setSignals] = useState([]);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);

  const ae = control?.auto_evidence;
  const status = ae?.status || 'pending';
  const meta = statusMeta[status] || statusMeta.pending;
  const hasEvidence = Boolean(ae);

  // Fetch static remediation for each signal whenever the control's auto_evidence changes
  const fetchRemediations = useCallback(async () => {
    if (!control?.id) return;
    try {
      const resp = await axios.get(`${API}/controls/${control.id}/remediation`, authHeaders());
      setSignals(resp.data?.signals || []);
    } catch (_) {
      // Remediation is best-effort; fall back to raw matches
      setSignals((ae?.matches || []).map((m) => ({ ...m, remediation: null })));
    }
  }, [control?.id, ae?.status, ae?.evaluated_at]);

  useEffect(() => {
    fetchRemediations();
  }, [fetchRemediations]);

  // Re-collect & Re-evaluate: kick off collection, poll until completion, then refresh
  const handleRecollectAndEvaluate = async () => {
    setRecollecting(true);
    setJobStatus('starting');
    let jobId = null;
    try {
      const resp = await axios.post(
        `${API}/controls/${control.id}/recollect-and-evaluate`,
        {},
        authHeaders()
      );
      jobId = resp.data.job_id;
      toast.success('Re-collection started');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to start re-collection');
      setRecollecting(false);
      setJobStatus(null);
      return;
    }

    // Poll
    const start = Date.now();
    const maxMs = 5 * 60 * 1000; // 5 min ceiling
    while (Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const jr = await axios.get(`${API}/technologies/evidence-jobs/${jobId}`, authHeaders());
        const st = jr.data?.status;
        setJobStatus(st);
        if (st === 'completed' || st === 'succeeded' || st === 'done') {
          toast.success('Evidence re-collected and re-evaluated');
          break;
        }
        if (st === 'failed' || st === 'error') {
          toast.error(jr.data?.error || 'Re-collection failed');
          break;
        }
      } catch (_) {
        // keep polling
      }
    }
    // Refresh the control so parent sees new auto_evidence
    try {
      const cr = await axios.get(`${API}/controls/${control.id}`, authHeaders());
      if (onEvaluated) onEvaluated(cr.data?.auto_evidence);
    } catch (_) {
      // noop
    }
    setRecollecting(false);
    setJobStatus(null);
  };

  return (
    <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm" data-testid="auto-evidence-panel">
      <CardHeader className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            <span>Auto-Collected Evidence</span>
            <Badge className={meta.className} data-testid="auto-evidence-status-badge">
              <span className="mr-1">{meta.icon}</span>
              {meta.label}
            </Badge>
            {ae?.ai_fallback && (
              <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                <Sparkles className="w-3 h-3 mr-1" />
                AI-matched
              </Badge>
            )}
          </CardTitle>
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
        <CardDescription>
          Automatically evaluated from connected technology evidence (no manual upload needed)
        </CardDescription>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {!hasEvidence ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600" data-testid="auto-evidence-empty">
              <p className="font-medium text-slate-800 mb-1">Not yet evaluated</p>
              <p className="text-xs">
                Click <b>Re-collect & Re-evaluate</b> below to pull fresh evidence from the connected
                technology and run the matcher.
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="auto-evidence-proof">
                <p className="text-slate-800">{ae.proof || '—'}</p>
                {ae.ai_reasoning && (
                  <p className="mt-2 text-xs text-slate-500 italic">AI reasoning: {ae.ai_reasoning}</p>
                )}
              </div>

              {ae.what_would_fix && (
                <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm" data-testid="auto-evidence-fix-hint">
                  <p className="text-xs font-medium uppercase tracking-wide text-orange-700 mb-1">
                    Summary
                  </p>
                  <p className="text-slate-800">{ae.what_would_fix}</p>
                </div>
              )}

              {(signals.length > 0 || (ae.matches || []).length > 0) && (
                <div className="space-y-2" data-testid="auto-evidence-matches">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Evidence Signals</p>
                  {(signals.length ? signals : (ae.matches || []).map(m => ({ ...m, remediation: null }))).map((m, idx) => (
                    <div key={idx} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">{m.check_id}</span>
                        {m.status && (
                          <Badge className={statusMeta[m.status]?.className || statusMeta.pending.className}>
                            {statusMeta[m.status]?.label || m.status}
                          </Badge>
                        )}
                      </div>
                      {(m.pass_repos || m.fail_repos) && (
                        <div className="mt-1 text-slate-500">
                          {m.pass_repos?.length > 0 && (
                            <span className="mr-3">✓ {m.pass_repos.length} pass</span>
                          )}
                          {m.fail_repos?.length > 0 && (
                            <span className="mr-3 text-red-600">✕ {m.fail_repos.length} fail</span>
                          )}
                          {typeof m.total_repos === 'number' && (
                            <span className="text-slate-400">of {m.total_repos} repos</span>
                          )}
                        </div>
                      )}
                      {m.fail_repos?.length > 0 && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[11px] text-slate-500">Show failing repos</summary>
                          <ul className="mt-1 list-disc pl-5 text-[11px] text-slate-600">
                            {m.fail_repos.slice(0, 25).map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </details>
                      )}
                      {m.flagged?.length > 0 && (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Flagged: {m.flagged.join(', ')}
                        </div>
                      )}

                      {/* Per-signal remediation accordion */}
                      <SignalRemediation signal={m} controlId={control.id} />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-200">
                <div>
                  {ae.rule_key && (
                    <span className="mr-3">
                      Rule: <code className="bg-slate-100 px-1 rounded">{ae.rule_key}</code>
                    </span>
                  )}
                  {ae.evidence_collected_at && (
                    <span className="mr-3">
                      Evidence: {new Date(ae.evidence_collected_at).toLocaleString()}
                    </span>
                  )}
                  {ae.evaluated_at && (
                    <span>
                      Evaluated: {new Date(ae.evaluated_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              size="sm"
              onClick={handleRecollectAndEvaluate}
              disabled={recollecting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="auto-evidence-recollect-btn"
            >
              {recollecting ? (
                <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> {jobStatus === 'running' ? 'Collecting…' : 'Re-collecting…'}</>
              ) : (
                <><Zap className="w-3 h-3 mr-2" /> Re-collect & Re-evaluate</>
              )}
            </Button>
            {hasEvidence && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSnapshotDialogOpen(true)}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="auto-evidence-view-source-btn"
              >
                <FileSearch className="w-3 h-3 mr-2" />
                View collected evidence
              </Button>
            )}
            {hasEvidence && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/controls/${control.id}/evidence-snapshot`, authHeaders());
                    const source = res.data?.source;
                    const evId = res.data?.evidence_id || 'latest';
                    if (!source) { toast.error('No evidence snapshot for this control'); return; }
                    const token = localStorage.getItem('token');
                    // Fetch PDF as a blob so the Authorization header is applied
                    const pdfRes = await fetch(`${API}/evidence/${source}/${evId}/report.pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!pdfRes.ok) throw new Error(await pdfRes.text());
                    const blob = await pdfRes.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `${source}-evidence-${(evId || 'snapshot').slice(0, 20)}.pdf`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('Evidence PDF downloaded');
                  } catch (e) {
                    toast.error('Failed to download evidence PDF');
                  }
                }}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="auto-evidence-download-pdf-btn"
              >
                <FileSearch className="w-3 h-3 mr-2" />
                Download PDF
              </Button>
            )}
            {hasEvidence && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/controls/${control.id}/evidence-snapshot`, authHeaders());
                    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `evidence-snapshot-${control.id.slice(0, 8)}.json`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('Snapshot JSON downloaded');
                  } catch (e) {
                    toast.error('Failed to download snapshot JSON');
                  }
                }}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="auto-evidence-download-json-btn"
              >
                <FileSearch className="w-3 h-3 mr-2" />
                Download JSON
              </Button>
            )}
            {recollecting && (
              <span className="text-[11px] text-slate-500">
                Pulling fresh evidence from the connected technology — this can take up to a minute.
              </span>
            )}
          </div>
        </CardContent>
      )}

      <EvidenceSourceDialog
        open={sourceDialogOpen}
        onClose={() => setSourceDialogOpen(false)}
        controlId={control?.id}
        controlName={control?.control_name}
      />
      <EvidenceSnapshotDialog
        open={snapshotDialogOpen}
        onClose={() => setSnapshotDialogOpen(false)}
        controlId={control?.id}
        controlName={control?.control_name}
      />
    </Card>
  );
};

export default AutoEvidencePanel;
